import { ADMIN_GROUPS, type AdminRole } from '#shared/adminPermissions';
import { PLACEHOLDER_EVENT_DATE } from '#shared/constants';
import { computeExpiresAt } from '#shared/listingExpiry';
import type {
  AdminUserRecord,
  EventInput,
  EventRecord,
  ListingRecord,
  ListingSubmission,
} from '#shared/types';
import { termFromDate } from '#shared/vicDates';
import type { VicDatesSnapshot } from '#shared/vicDatesTypes';
import { LOCAL_ADMIN_EMAIL, LOCAL_ADMIN_ROLE } from '#src/utils/localAdmin';

import committedVicDates from '../public/events/vic-dates.json';

let eventId = 3;
let listingId = 3;

const now = () => new Date().toISOString();

const cloneListings = (): ListingRecord[] => [
  {
    id: 'pending-1',
    name: 'Lee Lawn Care',
    serviceType: 'Gardening',
    phone: '0411 000 111',
    email: 'jamie.demo@example.com',
    blurb: 'Weekly mowing and garden tidy-ups for local families.',
    status: 'pending',
    submittedAt: '2026-07-28T09:00:00.000Z',
    expiryMonths: 12,
    expiresAt: '2027-07-28T09:00:00.000Z',
  },
  {
    id: 'pending-2',
    name: 'Sam Ortiz',
    serviceType: 'Tutoring',
    email: 'sam.demo@example.com',
    blurb: 'Year 3–6 maths tutoring after school.',
    status: 'pending',
    submittedAt: '2026-07-29T11:30:00.000Z',
    expiryMonths: 6,
    expiresAt: '2027-01-29T11:30:00.000Z',
  },
  {
    id: 'approved-1',
    name: 'Oak Grove Tutoring',
    serviceType: 'Tutoring',
    phone: '0412 345 678',
    email: 'sarah.demo@example.com',
    blurb:
      'Primary maths and English tutoring. After-school sessions available near the school.',
    status: 'approved',
    submittedAt: '2026-07-20T08:00:00.000Z',
    expiryMonths: 12,
    expiresAt: '2027-07-20T08:00:00.000Z',
    moderatedAt: '2026-07-21T10:00:00.000Z',
    moderatedBy: LOCAL_ADMIN_EMAIL,
  },
];

const cloneEvents = (): EventRecord[] => [
  {
    id: 'draft-1',
    title: 'Trivia Night (draft)',
    description: 'A fun evening for parents and friends. Details TBA.',
    dates: ['2026-08-15'],
    date: '2026-08-15',
    term: 3,
    year: 2026,
    status: 'draft',
    createdAt: '2026-07-25T00:00:00.000Z',
    updatedAt: '2026-07-25T00:00:00.000Z',
  },
  {
    id: 'draft-2',
    title: 'Spring Picnic (draft)',
    description: '',
    dates: [],
    date: PLACEHOLDER_EVENT_DATE,
    term: 4,
    year: 2026,
    status: 'draft',
    createdAt: '2026-07-26T00:00:00.000Z',
    updatedAt: '2026-07-26T00:00:00.000Z',
  },
  {
    id: 'published-1',
    title: 'Working Bee',
    description:
      'Working bees bring families together to help care for and improve our school grounds.',
    dates: ['2026-05-17'],
    date: '2026-05-17',
    term: 2,
    year: 2026,
    highlight: true,
    status: 'published',
    createdAt: '2026-04-01T00:00:00.000Z',
    updatedAt: '2026-04-10T00:00:00.000Z',
    publishedAt: '2026-04-10T00:00:00.000Z',
  },
];

const cloneUsers = (): AdminUserRecord[] => [
  {
    username: LOCAL_ADMIN_EMAIL,
    email: LOCAL_ADMIN_EMAIL,
    role: LOCAL_ADMIN_ROLE,
    status: 'enabled',
    enabled: true,
    createdAt: '2026-01-01T00:00:00.000Z',
  },
  {
    username: 'content@example.com',
    email: 'content@example.com',
    role: ADMIN_GROUPS.contentAdmin,
    status: 'enabled',
    enabled: true,
    createdAt: '2026-02-01T00:00:00.000Z',
  },
  {
    username: 'pending@example.com',
    email: 'pending@example.com',
    role: ADMIN_GROUPS.contentAdmin,
    status: 'force_change_password',
    enabled: true,
    createdAt: '2026-07-01T00:00:00.000Z',
  },
];

let listings = cloneListings();
let events = cloneEvents();
let users = cloneUsers();

const requireListing = (id: string): ListingRecord => {
  const listing = listings.find((item) => item.id === id);
  if (!listing) {
    throw new Error('Listing not found');
  }
  return listing;
};

const requireEvent = (id: string): EventRecord => {
  const event = events.find((item) => item.id === id);
  if (!event) {
    throw new Error('Event not found');
  }
  return event;
};

const requireUser = (username: string): AdminUserRecord => {
  const user = users.find(
    (item) => item.username === username || item.email === username,
  );
  if (!user) {
    throw new Error('User not found');
  }
  return user;
};

const vicDatesSnapshot = committedVicDates as VicDatesSnapshot;

const deriveTermYear = (
  dates: string[],
): Pick<EventInput, 'term' | 'year'> => {
  const earliest = [...dates]
    .filter((value) => value && value !== PLACEHOLDER_EVENT_DATE)
    .sort((a, b) => a.localeCompare(b))[0];
  if (!earliest) {
    return {};
  }
  const lookup = termFromDate(earliest, vicDatesSnapshot);
  return lookup ? { term: lookup.term, year: lookup.year } : {};
};

const applyEventInput = (
  event: EventRecord,
  input: EventInput,
  status: EventRecord['status'],
): EventRecord => {
  const dates = input.dates ?? event.dates ?? [];
  const date =
    input.date ??
    (dates.length > 0 ? dates[0] : (event.date ?? PLACEHOLDER_EVENT_DATE));
  const derived = deriveTermYear(dates);

  return {
    ...event,
    title: input.title ?? event.title,
    description: input.description ?? event.description,
    dates,
    date,
    term: derived.term ?? event.term,
    year: derived.year ?? event.year,
    highlight: input.highlight ?? event.highlight,
    imageKey: input.imageKey ?? event.imageKey,
    status,
    updatedAt: now(),
  };
};

export const dummyFetchAdminListings = async (
  status: string,
): Promise<ListingRecord[]> =>
  listings.filter((listing) => listing.status === status);

export const dummyApproveListing = async (id: string): Promise<void> => {
  const listing = requireListing(id);
  listing.status = 'approved';
  listing.moderatedAt = now();
  listing.moderatedBy = LOCAL_ADMIN_EMAIL;
};

export const dummyDeclineListing = async (
  id: string,
  moderatorNote?: string,
): Promise<void> => {
  const listing = requireListing(id);
  listing.status = 'declined';
  listing.moderatedAt = now();
  listing.moderatedBy = LOCAL_ADMIN_EMAIL;
  listing.moderatorNote = moderatorNote;
};

export const dummyUnpublishListing = async (id: string): Promise<void> => {
  const listing = requireListing(id);
  listing.status = 'declined';
  listing.moderatedAt = now();
  listing.moderatedBy = LOCAL_ADMIN_EMAIL;
};

export const dummyUpdateListing = async (
  id: string,
  updates: Partial<ListingRecord>,
): Promise<void> => {
  const listing = requireListing(id);
  const nextUpdates = { ...updates };

  if (
    nextUpdates.expiryMonths !== undefined &&
    typeof nextUpdates.expiryMonths === 'number'
  ) {
    nextUpdates.expiresAt = computeExpiresAt(
      listing.submittedAt,
      nextUpdates.expiryMonths,
    );
  }

  Object.assign(listing, nextUpdates, {
    id: listing.id,
    status: listing.status,
  });
};

export const dummyCreateListing = async (
  input: ListingSubmission,
): Promise<ListingRecord> => {
  const timestamp = now();
  const expiryMonths = input.expiryMonths ?? 12;
  const created: ListingRecord = {
    id: `local-listing-${listingId++}`,
    name: input.name,
    serviceType: input.serviceType,
    phone: input.phone,
    email: input.email,
    website: input.website,
    blurb: input.blurb,
    imageKey: input.imageKey,
    expiryMonths,
    expiresAt: computeExpiresAt(timestamp, expiryMonths),
    status: 'pending',
    submittedAt: timestamp,
    moderatedAt: timestamp,
    moderatedBy: LOCAL_ADMIN_EMAIL,
  };
  listings = [created, ...listings];
  return created;
};

export const dummyFetchAdminEvents = async (
  status: 'draft' | 'published',
): Promise<EventRecord[]> => events.filter((event) => event.status === status);

export const dummyCreateEvent = async (
  input: EventInput,
): Promise<EventRecord> => {
  const timestamp = now();
  const dates = input.dates ?? [];
  const derived = deriveTermYear(dates);
  const created: EventRecord = {
    id: `local-event-${eventId++}`,
    title: input.title,
    description: input.description,
    dates,
    date: input.date ?? dates[0] ?? PLACEHOLDER_EVENT_DATE,
    term: derived.term,
    year: derived.year,
    highlight: input.highlight,
    imageKey: input.imageKey,
    status: 'draft',
    createdAt: timestamp,
    updatedAt: timestamp,
  };
  events = [created, ...events];
  return created;
};

export const dummyUpdateEvent = async (
  id: string,
  input: EventInput,
): Promise<void> => {
  const existing = requireEvent(id);
  events = events.map((event) =>
    event.id === id ? applyEventInput(existing, input, existing.status) : event,
  );
};

export const dummyPublishEvent = async (
  id: string,
  input: EventInput,
): Promise<void> => {
  const existing = requireEvent(id);
  const published = applyEventInput(existing, input, 'published');
  published.publishedAt = now();
  if (published.highlight) {
    events = events.map((event) =>
      event.id === id
        ? published
        : {
            ...event,
            highlight: event.status === 'published' ? false : event.highlight,
          },
    );
    return;
  }
  events = events.map((event) => (event.id === id ? published : event));
};

export const dummyUnpublishEvent = async (id: string): Promise<void> => {
  const existing = requireEvent(id);
  events = events.map((event) =>
    event.id === id
      ? {
          ...existing,
          status: 'draft',
          highlight: false,
          updatedAt: now(),
          publishedAt: undefined,
        }
      : event,
  );
};

export const dummyDeleteEvent = async (id: string): Promise<void> => {
  const existing = requireEvent(id);
  if (existing.status !== 'draft') {
    throw new Error('Only draft events can be deleted. Unpublish first.');
  }
  events = events.filter((event) => event.id !== id);
};

export const dummyFetchAdminUsers = async (): Promise<AdminUserRecord[]> => [
  ...users,
];

export const dummyCreateAdminUser = async (input: {
  email: string;
  role: AdminRole;
}): Promise<AdminUserRecord> => {
  if (users.some((user) => user.email === input.email)) {
    throw new Error('A user with that email already exists');
  }
  const created: AdminUserRecord = {
    username: input.email,
    email: input.email,
    role: input.role,
    status: 'force_change_password',
    enabled: true,
    createdAt: now(),
  };
  users = [...users, created];
  return created;
};

export const dummyUpdateAdminUserRole = async (
  username: string,
  role: AdminRole,
): Promise<void> => {
  const user = requireUser(username);
  if (user.email === LOCAL_ADMIN_EMAIL && role !== LOCAL_ADMIN_ROLE) {
    throw new Error('You cannot demote your own account');
  }
  user.role = role;
};

export const dummyEnableAdminUser = async (username: string): Promise<void> => {
  const user = requireUser(username);
  if (user.email === LOCAL_ADMIN_EMAIL) {
    throw new Error('You cannot change your own account status');
  }
  user.enabled = true;
  user.status = 'enabled';
};

export const dummyDisableAdminUser = async (
  username: string,
): Promise<void> => {
  const user = requireUser(username);
  if (user.email === LOCAL_ADMIN_EMAIL) {
    throw new Error('You cannot change your own account status');
  }
  user.enabled = false;
  user.status = 'disabled';
};

export const dummyResetAdminUserPassword = async (
  _username: string,
): Promise<void> => {
  requireUser(_username);
};

export const dummyDeleteAdminUser = async (username: string): Promise<void> => {
  const user = requireUser(username);
  if (user.email === LOCAL_ADMIN_EMAIL) {
    throw new Error('You cannot delete your own account');
  }
  users = users.filter(
    (item) => item.username !== user.username && item.email !== user.email,
  );
};

export const dummyPresignUpload = async (
  contentType: string,
): Promise<{ url: string; fields: Record<string, string>; key: string }> => ({
  url: 'local-dummy-upload',
  fields: { 'Content-Type': contentType },
  key: `pending/local-${Date.now()}`,
});
